import type { JSX } from 'react';
import type { StageSummaryDto } from '@/types/api';

function count(current: number, cap?: number, total?: number): string {
  const scoped = cap === undefined ? `×${current}` : `${current} of ${cap}`;
  return total !== undefined && total > current ? `${total} · ${scoped} this attempt` : scoped;
}

export function StageActivityChips({ stage }: { stage: StageSummaryDto }): JSX.Element | null {
  const chips: Array<{ key: string; text: string; tone: 'neutral' | 'warn' }> = [];

  if (stage.reworks_requested !== undefined && stage.reworks_requested > 0) {
    chips.push({
      key: 'rework',
      text: `↺ requested rework ${count(
        stage.reworks_requested,
        stage.rework_cap,
        stage.reworks_total
      )}`,
      tone: 'neutral'
    });
  }

  if (stage.self_verify_loops !== undefined) {
    chips.push({
      key: 'selfverify',
      text: `⟳ self-verify ${count(
        stage.self_verify_loops,
        stage.self_verify_cap,
        stage.self_verify_total
      )}${stage.cap_reached ? ' (cap reached)' : ''}`,
      tone: stage.cap_reached ? 'warn' : 'neutral'
    });
  }

  if (stage.failed_sub_stage !== undefined && stage.failed_sub_stage.length > 0) {
    chips.push({
      key: 'substage',
      text: `failed in ${stage.failed_sub_stage}`,
      tone: 'warn'
    });
  }

  if (chips.length === 0) return null;

  return (
    <>
      {chips.map((c) => (
        <span
          key={c.key}
          data-testid={`stage-activity-${c.key}-${stage.stage_id}`}
          className={`inline-flex items-center gap-1 font-mono text-[10px] px-2 py-0.5 rounded-full ${
            c.tone === 'warn'
              ? 'bg-warn/[0.14] text-warn'
              : 'bg-role-architect/[0.12] text-role-architect'
          }`}
        >
          {c.text}
        </span>
      ))}
    </>
  );
}
